// RSA-OAEP password encryption for request bodies.
// Scheme: RSA-OAEP, 2048-bit, SHA-256 (OAEP + MGF1), no label, UTF-8 plaintext,
// standard Base64 ciphertext. Must match the backend RequestCrypto contract.
//
// Public key body (base64, no PEM headers) is supplied via the pipeline
// variable REACT_APP_ENCRYPTION_PUBLIC_KEY. The same variable exists in
// Blitzz_Admin (REACT_APP_ENCRYPTION_PUBLIC_KEY) and Blitzz_Superadmin
// (VITE_APP_ENCRYPTION_PUBLIC_KEY) — rotate all 3 together.

const MAX_PLAINTEXT_BYTES = 190;

let cachedKeyPromise = null;

export function getPublicKey() {
    const body = process.env.REACT_APP_ENCRYPTION_PUBLIC_KEY;
    if (!body) {
        console.error("[requestCrypto] Encryption public key is not configured");
        throw new Error("Something went wrong. Please try again later.");
    }
    return `-----BEGIN PUBLIC KEY-----\n${body}\n-----END PUBLIC KEY-----`;
}

function pemToArrayBuffer(pem) {
    const b64 = pem
        .replace(/-----BEGIN PUBLIC KEY-----/, "")
        .replace(/-----END PUBLIC KEY-----/, "")
        .replace(/\s+/g, "");
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
}

function importPublicKey() {
    return window.crypto.subtle.importKey(
        "spki",
        pemToArrayBuffer(getPublicKey()),
        { name: "RSA-OAEP", hash: "SHA-256" },
        false,
        ["encrypt"]
    );
}

function ensureKey() {
    if (!cachedKeyPromise) {
        cachedKeyPromise = importPublicKey().catch((err) => {
            cachedKeyPromise = null;
            throw err;
        });
    }
    return cachedKeyPromise;
}

export async function encryptPassword(plaintext) {
    if (typeof plaintext !== "string" || plaintext.length === 0) {
        console.error("[requestCrypto] Plaintext must be a non-empty string");
        throw new Error("Something went wrong. Please try again later.");
    }
    const data = new TextEncoder().encode(plaintext);
    if (data.byteLength > MAX_PLAINTEXT_BYTES) {
        console.error(`[requestCrypto] Plaintext exceeds ${MAX_PLAINTEXT_BYTES} bytes`);
        throw new Error("Something went wrong. Please try again later.");
    }
    const key = await ensureKey();
    const buf = await window.crypto.subtle.encrypt({ name: "RSA-OAEP" }, key, data);
    const bytes = new Uint8Array(buf);
    let binary = "";
    for (let i = 0; i < bytes.byteLength; i += 1) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
}
